import React, { useCallback, useEffect, useState } from 'react';
import { SettingsAPI } from '../services/api';
import { TFN } from '../types/payment';
import { AlertService } from '../services/AlertService';
import { ConfirmationDialog } from './ConfirmationDialog';

/**
 * Toll-Free Number (TFN) management — CRUD for the managed list that backs the
 * mandatory TFN dropdown on the payment form. Rendered in Settings for
 * settings-access roles. Read is open to everyone; write is enforced server-side.
 */
export const TFNSettings: React.FC = () => {
  const [tfns, setTfns] = useState<TFN[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Add form
  const [newNumber, setNewNumber] = useState('');
  const [newLabel, setNewLabel] = useState('');

  // Inline edit
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editNumber, setEditNumber] = useState('');
  const [editLabel, setEditLabel] = useState('');

  const [tfnToDelete, setTfnToDelete] = useState<TFN | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await SettingsAPI.getTFNs(); // all (active + inactive)
      setTfns(res.tfns || []);
    } catch (error: any) {
      AlertService.error(error?.message || 'Failed to load TFNs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!newNumber.trim()) {
      AlertService.error('TFN number is required');
      return;
    }
    setSaving(true);
    try {
      await SettingsAPI.createTFN({ number: newNumber.trim(), label: newLabel.trim() || undefined });
      setNewNumber('');
      setNewLabel('');
      AlertService.success('TFN added');
      await load();
    } catch (error: any) {
      AlertService.error(error?.message || 'Failed to add TFN');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (t: TFN) => {
    setEditingId(t.id);
    setEditNumber(t.number);
    setEditLabel(t.label || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditNumber('');
    setEditLabel('');
  };

  const saveEdit = async (t: TFN) => {
    if (!editNumber.trim()) {
      AlertService.error('TFN number is required');
      return;
    }
    setSaving(true);
    try {
      await SettingsAPI.updateTFN(t.id, { number: editNumber.trim(), label: editLabel.trim() });
      AlertService.success('TFN updated');
      cancelEdit();
      await load();
    } catch (error: any) {
      AlertService.error(error?.message || 'Failed to update TFN');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (t: TFN) => {
    setSaving(true);
    try {
      await SettingsAPI.updateTFN(t.id, { is_active: !t.is_active });
      await load();
    } catch (error: any) {
      AlertService.error(error?.message || 'Failed to update TFN');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!tfnToDelete) return;
    setSaving(true);
    try {
      await SettingsAPI.deleteTFN(tfnToDelete.id);
      AlertService.success('TFN deleted');
      setTfnToDelete(null);
      await load();
    } catch (error: any) {
      AlertService.error(error?.message || 'Failed to delete TFN');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="setting-group">
      <div className="setting-header">
        <h3>Toll-Free Numbers (TFN)</h3>
      </div>
      <div className="setting-description">
        <p>Manage the Toll-Free Numbers agents select on the payment form. Inactive TFNs stay on historical records but no longer appear in the dropdown.</p>
      </div>

      {/* Add form */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', margin: '0.75rem 0' }}>
        <input
          type="text"
          placeholder="Number (e.g. 1-800-555-0100)"
          value={newNumber}
          onChange={(e) => setNewNumber(e.target.value)}
          style={{ flex: '1 1 200px', padding: '0.4rem 0.6rem' }}
        />
        <input
          type="text"
          placeholder="Label (optional)"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          style={{ flex: '1 1 200px', padding: '0.4rem 0.6rem' }}
        />
        <button type="button" className="btn-primary" disabled={saving} onClick={handleAdd}>
          {saving ? 'Saving…' : 'Add TFN'}
        </button>
      </div>

      {loading ? (
        <p>Loading TFNs…</p>
      ) : tfns.length === 0 ? (
        <p>No TFNs yet. Add one above so agents can select it on the payment form.</p>
      ) : (
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Number</th>
              <th>Label</th>
              <th>Status</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tfns.map((t) => (
              <tr key={t.id}>
                <td>
                  {editingId === t.id ? (
                    <input type="text" value={editNumber} onChange={(e) => setEditNumber(e.target.value)} style={{ width: '100%' }} />
                  ) : t.number}
                </td>
                <td>
                  {editingId === t.id ? (
                    <input type="text" value={editLabel} onChange={(e) => setEditLabel(e.target.value)} style={{ width: '100%' }} />
                  ) : (t.label || '—')}
                </td>
                <td>
                  <span className={`status-badge ${t.is_active ? 'auto' : 'manual'}`}>
                    {t.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {editingId === t.id ? (
                    <>
                      <button type="button" className="btn-primary" disabled={saving} onClick={() => saveEdit(t)} style={{ marginRight: '0.4rem' }}>Save</button>
                      <button type="button" className="btn-secondary" disabled={saving} onClick={cancelEdit}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <button type="button" className="btn-secondary" disabled={saving} onClick={() => startEdit(t)} style={{ marginRight: '0.4rem' }}>Edit</button>
                      <button type="button" className="btn-secondary" disabled={saving} onClick={() => toggleActive(t)} style={{ marginRight: '0.4rem' }}>
                        {t.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button type="button" className="btn-danger" disabled={saving} onClick={() => setTfnToDelete(t)}>Delete</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ConfirmationDialog
        isOpen={!!tfnToDelete}
        title="Delete TFN"
        message={`Delete TFN "${tfnToDelete?.label ? `${tfnToDelete.label} (${tfnToDelete.number})` : tfnToDelete?.number}"? Historical records keep their stored value.`}
        confirmText="Delete"
        onConfirm={confirmDelete}
        onCancel={() => setTfnToDelete(null)}
      />
    </div>
  );
};
